'use client';

/**
 * LoadingOverlay Component
 *
 * A reusable semi-transparent backdrop with centered spinner for indicating
 * loading states within containers. Replaces duplicated inline implementations
 * in KanbanBoard, ChartContainer, and similar components.
 *
 * Uses sx-prop styling (not styled()) per project convention.
 */

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React from 'react';
import type { NeonAccent } from '../../theme/palette';
import { getNeonStyles } from '../../theme/palette';

// ============================================================================
// Types
// ============================================================================

export interface LoadingOverlayProps {
  /** Show or hide the overlay */
  visible: boolean;
  /** Optional text displayed below the spinner */
  message?: string;
  /** Custom spinner element (replaces default CircularProgress) */
  spinner?: React.ReactNode;
  /** Spinner size in pixels (default 40) */
  size?: number;
  /** Backdrop blur in pixels (default 4) */
  blur?: number;
  /** Neon accent color for the spinner */
  neonAccent?: NeonAccent;
  /** Border radius matching parent container (default 2 = theme spacing unit) */
  borderRadius?: number | string;
  /** Additional MUI sx overrides */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_SIZE = 40;
const DEFAULT_BLUR = 4;
const DEFAULT_BORDER_RADIUS = 2;

// ============================================================================
// Component
// ============================================================================

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  visible,
  message,
  spinner,
  size = DEFAULT_SIZE,
  blur = DEFAULT_BLUR,
  neonAccent,
  borderRadius = DEFAULT_BORDER_RADIUS,
  sx,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  if (!visible) {
    return null;
  }

  // Resolve spinner color: neonAccent base color or theme primary
  const spinnerColor = neonAccent ? getNeonStyles(neonAccent, isDark).base : theme.palette.primary.main;

  // Theme-aware backdrop
  const backdropColor = isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)';

  return (
    <Box
      role="status"
      aria-busy="true"
      aria-label={message ?? 'Loading'}
      sx={[
        {
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          bgcolor: backdropColor,
          backdropFilter: `blur(${blur}px)`,
          borderRadius,
          zIndex: 10,
        },
        // Merge caller-provided sx (supports array and object forms)
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {spinner ?? (
        <CircularProgress
          size={size}
          sx={{
            color: spinnerColor,
            '@media (prefers-reduced-motion: reduce)': {
              animationDuration: '4s',
            },
          }}
        />
      )}
      {message && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
            textAlign: 'center',
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );
};

LoadingOverlay.displayName = 'LoadingOverlay';

export default LoadingOverlay;
